import React, { useEffect, useState } from "react";
import { FetchMedia } from "../../hooks/APIHooks";

const StarPriceCard = ({ level, price }) => {
  const getStarColor = () => {
    switch (level) {
      case 1:
        return "from-yellow-100 via-yellow-200 to-yellow-300";
      case 2:
        return "from-blue-100 via-blue-200 to-blue-300";
      case 3:
        return "from-purple-100 via-purple-200 to-purple-300";
      default:
        return "from-gray-100 via-gray-200 to-gray-300";
    }
  };

  const getTextColor = () => {
    switch (level) {
      case 1:
        return "text-yellow-800";
      case 2:
        return "text-blue-800";
      case 3:
        return "text-purple-800";
      default:
        return "text-gray-800";
    }
  };

  const getBorderColor = () => {
    switch (level) {
      case 1:
        return "border-yellow-300";
      case 2:
        return "border-blue-300";
      case 3:
        return "border-purple-300";
      default:
        return "border-gray-300";
    }
  };

  const getStarSize = () => {
    switch (level) {
      case 1:
        return "text-5xl";
      case 2:
        return "text-6xl";
      case 3:
        return "text-7xl";
      default:
        return "text-5xl";
    }
  };

  return (
    <div
      className={`relative group cursor-pointer w-full aspect-square rounded-2xl p-6 flex flex-col items-center justify-center border-2 ${getBorderColor()} bg-gradient-to-br ${getStarColor()} transition-all duration-300 hover:scale-[1.03] hover:shadow-2xl shadow-lg`}
    >
      {/* Big Star Background */}
      <div
        className={`absolute inset-0 flex items-center justify-center ${getStarSize()} ${getTextColor()} opacity-20`}
      >
        ★
      </div>

      <div className="relative z-10 text-center w-full">
        {/* Level Badge */}
        <div
          className={`inline-block px-4 py-1 rounded-full ${
            level === 2
              ? "bg-blue-600"
              : level === 3
              ? "bg-purple-600"
              : "bg-yellow-600"
          } text-white text-sm font-bold mb-4`}
        >
          {level}st Level
        </div>

        {/* Big Price */}
        <div className={`${getTextColor()} font-bold text-4xl mb-2`}>
          {price}
        </div>

        {/* Star Rating */}
        <div className="flex justify-center gap-1 mb-6">
          {[...Array(level)].map((_, i) => (
            <span
              key={i}
              className={`text-2xl ${
                level === 1
                  ? "text-yellow-500"
                  : level === 2
                  ? "text-blue-500"
                  : "text-purple-500"
              } animate-bounce`}
              style={{ animationDelay: `${i * 0.1}s` }}
            >
              ★
            </span>
          ))}
        </div>

        {/* CTA Button */}
        <button
          className={`px-6 py-3 rounded-xl font-bold text-white transition-all duration-300 ${
            level === 1
              ? "bg-yellow-600 hover:bg-yellow-700"
              : level === 2
              ? "bg-blue-600 hover:bg-blue-700"
              : "bg-purple-600 hover:bg-purple-700"
          } transform hover:-translate-y-1`}
        >
          Get Level {level}
        </button>
      </div>
    </div>
  );
};

const OfferBanner = () => {
  const [images, setImages] = useState([]);

  useEffect(() => {
    fetch(FetchMedia)
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.files) {
          setImages(data.files);
        }
      })
      .catch((err) => console.error("Error loading images:", err));
  }, []);

  const priceLevels = [
    {
      level: 1,
      price: "₹1,950",
    },
    {
      level: 2,
      price: "₹52,650",
    },
    {
      level: 3,
      price: "₹9,84,150",
    },
  ];

  return (
    <div className=" py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-secondary mb-3">
            Exclusive Price Levels
          </h1>
          <div className="w-24 h-1 bg-gradient-to-r from-yellow-400 via-blue-500 to-purple-600 mx-auto mb-4 rounded-full"></div>
          <p className="text-lg text-secondary max-w-2xl mx-auto">
            Choose your level and unlock amazing cash back rewards with our
            star-rated packages
          </p>
        </div>

        {/* Price Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {priceLevels.map((levelData) => (
            <StarPriceCard key={levelData.level} {...levelData} />
          ))}
        </div>

        {/* Info Section */}
        <div className="max-w-4xl mx-auto bg-gradient-to-r from-yellow-50 via-blue-50 to-purple-50 rounded-2xl p-8 mb-16 border border-gray-200 shadow-lg">
          <div className="text-center">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">
              ✨ How It Works
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-4">
                <div className="text-3xl mb-2">⭐</div>
                <h4 className="font-semibold text-gray-700 mb-2">
                  Choose Your Level
                </h4>
                <p className="text-gray-600 text-sm">
                  Select from 1st, 2nd, or 3rd level based on your needs
                </p>
              </div>
              <div className="text-center p-4">
                <div className="text-3xl mb-2">💸</div>
                <h4 className="font-semibold text-gray-700 mb-2">
                  Get Cash Back
                </h4>
                <p className="text-gray-600 text-sm">
                  Enjoy cash back rewards with every purchase
                </p>
              </div>
              <div className="text-center p-4">
                <div className="text-3xl mb-2">🎁</div>
                <h4 className="font-semibold text-gray-700 mb-2">
                  Unlock Benefits
                </h4>
                <p className="text-gray-600 text-sm">
                  Access exclusive features and premium support
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Media Images Section */}
        <div className="max-w-5xl mx-auto">
          <h3 className="text-2xl font-bold text-center text-secondary mb-8">
            Latest Updates & Offers
          </h3>
          {images.map((img) => (
            <img
              key={img.id}
              src={img.file_path}
              alt={img.file_name}
              className="w-full mb-6 rounded-3xl shadow-xl object-cover max-h-96 hover:shadow-2xl transition-shadow duration-300"
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default OfferBanner;
